import { forwardRef, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../lib/styles';

const DEFAULT_MAX_HEIGHT = 100;
const TRANSITION_MS = 200;

export type SpoilerProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'maxHeight'> & {
  maxHeight?: number;
  showLabel: React.ReactNode;
  hideLabel: React.ReactNode;
  /** Merged with default button reset styles on the show/hide control */
  controlClassName?: string;
};

function rem(px: number): string {
  return `${px / 16}rem`;
}

const defaultState = { width: 0, height: 0 };

function useElementSize<T extends HTMLElement>() {
  const frameId = useRef(0);
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState(defaultState);

  const observer = useMemo(
    () =>
      new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          cancelAnimationFrame(frameId.current);
          frameId.current = requestAnimationFrame(() => {
            if (ref.current) {
              setSize({
                width: entry.contentRect.width,
                height: entry.contentRect.height,
              });
            }
          });
        }
      }),
    [],
  );

  useLayoutEffect(() => {
    const el = ref.current;

    if (el) {
      observer.observe(el);
      const { width, height } = el.getBoundingClientRect();
      setSize({ width, height });
    }

    return () => {
      observer.disconnect();

      if (frameId.current) {
        cancelAnimationFrame(frameId.current);
      }
    };
  }, [observer]);

  return { ref, width: size.width, height: size.height } as const;
}

export const Spoiler = forwardRef<HTMLDivElement, SpoilerProps>(function Spoiler(
  { className, children, maxHeight = DEFAULT_MAX_HEIGHT, hideLabel, showLabel, controlClassName, ...rest },
  ref,
) {
  const [show, setShow] = useState(false);
  const { ref: contentRef, height } = useElementSize<HTMLDivElement>();
  const spoiler = maxHeight < height;
  const spoilerMoreContent = show ? hideLabel : showLabel;

  const contentMaxHeight: React.CSSProperties['maxHeight'] = !show ? rem(maxHeight) : height ? rem(height) : undefined;

  return (
    <div ref={ref} className={cn('relative', className)} {...rest}>
      <div
        data-slot="spoiler-content"
        className="relative min-h-0"
        style={{
          maxHeight: contentMaxHeight,
          overflow: show ? undefined : 'hidden',
          transitionProperty: 'max-height',
          transitionTimingFunction: 'ease',
          transitionDuration: `${TRANSITION_MS}ms`,
        }}
      >
        <div ref={contentRef}>{children}</div>
      </div>

      {spoiler ? (
        <button
          type="button"
          data-spoiler-control
          onClick={() => {
            setShow((prev) => !prev);
          }}
          className={cn('cursor-pointer border-0 bg-transparent p-0 hover:underline', controlClassName)}
        >
          {spoilerMoreContent}
        </button>
      ) : null}
    </div>
  );
});
